import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ShieldCheck, X, ChevronDown, ChevronUp } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { LEGAL_VERSION, LEGAL_CHANGELOG } from "../lib/legal";
import api from "../lib/api";

const ACCEPT_KEY = "tp_legal_accepted_version";

/**
 * Shown once to a signed-in user whose stored legal acceptance is older than
 * the current LEGAL_VERSION. Acceptance is persisted server-side on the user
 * document AND cached in localStorage so it survives device switches.
 */
export default function ConsentRePrompt() {
  const { user, setUser } = useAuth();
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showDetails, setShowDetails] = useState(false);

  useEffect(() => {
    if (!user?.id && !user?.user_id) return;
    const uid = user.id || user.user_id;
    // Server-side acceptance is authoritative
    if (user.accepted_legal_version === LEGAL_VERSION) return;
    // Fall back to per-device cache so we don't nag between page loads before
    // the profile refresh lands
    let cached = null;
    try {
      const raw = localStorage.getItem(ACCEPT_KEY);
      const map = raw ? JSON.parse(raw) : {};
      cached = map[uid];
    } catch { /* ignore */ }
    if (cached === LEGAL_VERSION) return;
    setOpen(true);
  }, [user?.id, user?.user_id, user?.accepted_legal_version]);

  const accept = async () => {
    if (!user) return;
    const uid = user.id || user.user_id;
    setSaving(true);
    try {
      await api.post("/legal/accept", { version: LEGAL_VERSION });
      if (setUser) setUser({ ...user, accepted_legal_version: LEGAL_VERSION });
    } catch { /* offline — still store locally so we stop nagging */ }
    try {
      const raw = localStorage.getItem(ACCEPT_KEY);
      const map = raw ? JSON.parse(raw) : {};
      map[uid] = LEGAL_VERSION;
      localStorage.setItem(ACCEPT_KEY, JSON.stringify(map));
    } catch { /* storage full */ }
    setSaving(false);
    setOpen(false);
  };

  if (!open || !user) return null;
  const termsSections = LEGAL_CHANGELOG.terms.sections || [];
  const privacySections = LEGAL_CHANGELOG.privacy.sections || [];

  return (
    <div className="fixed inset-0 z-[9999] bg-zinc-900/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-4" data-testid="consent-reprompt">
      <div className="bg-white border border-zinc-300 shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto" role="dialog" aria-labelledby="consent-title">
        <div className="flex items-start justify-between p-5 border-b border-zinc-200">
          <div className="flex items-start gap-3">
            <ShieldCheck className="w-6 h-6 text-[#0e7490] shrink-0 mt-0.5" />
            <div>
              <h2 id="consent-title" className="font-heading font-900 text-xl leading-tight">
                We refreshed our Terms and Privacy Policy
              </h2>
              <p className="text-xs font-mono uppercase tracking-widest text-muted-foreground mt-1">
                Effective {LEGAL_CHANGELOG.terms.date}
              </p>
            </div>
          </div>
          <button
            onClick={() => setOpen(false)}
            className="text-zinc-400 hover:text-zinc-700 -mr-1"
            aria-label="Close"
            data-testid="consent-reprompt-close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 space-y-3 text-sm text-zinc-700">
          <p><strong className="text-zinc-900">Terms of Service:</strong> {LEGAL_CHANGELOG.terms.summary}</p>
          <p><strong className="text-zinc-900">Privacy Policy:</strong> {LEGAL_CHANGELOG.privacy.summary}</p>

          <button
            type="button"
            onClick={() => setShowDetails((v) => !v)}
            className="inline-flex items-center gap-1 text-primary font-700 text-sm hover:underline"
            data-testid="consent-reprompt-toggle-diff"
          >
            {showDetails ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            {showDetails ? "Hide section-by-section changes" : "See section-by-section changes"}
          </button>

          {showDetails && (
            <div className="border border-zinc-200 bg-zinc-50 p-4 space-y-4 text-xs" data-testid="consent-reprompt-diff">
              <div>
                <div className="font-700 text-zinc-900 uppercase tracking-wide mb-2">Terms of Service</div>
                <ul className="space-y-2">
                  {termsSections.map((s, i) => (
                    <li key={i}>
                      <div className="font-700 text-zinc-800">{s.heading}</div>
                      <p className="text-zinc-600 leading-relaxed">{s.change}</p>
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <div className="font-700 text-zinc-900 uppercase tracking-wide mb-2">Privacy Policy</div>
                <ul className="space-y-2">
                  {privacySections.map((s, i) => (
                    <li key={i}>
                      <div className="font-700 text-zinc-800">{s.heading}</div>
                      <p className="text-zinc-600 leading-relaxed">{s.change}</p>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}

          <p className="text-xs text-muted-foreground pt-2">
            Read the full{" "}
            <Link to="/legal/terms" className="text-primary font-700 hover:underline" data-testid="consent-reprompt-terms-link">Terms of Service</Link>{" "}
            and{" "}
            <Link to="/legal/privacy" className="text-primary font-700 hover:underline" data-testid="consent-reprompt-privacy-link">Privacy Policy</Link>.
            Continuing to use your account means you accept them.
          </p>
        </div>

        <div className="p-5 border-t border-zinc-200 flex flex-col-reverse sm:flex-row justify-end gap-2">
          <button
            onClick={() => setOpen(false)}
            className="px-4 py-2 text-sm font-700 border border-zinc-300 text-zinc-700 hover:bg-zinc-50"
            data-testid="consent-reprompt-later"
          >
            Remind me later
          </button>
          <button
            onClick={accept}
            disabled={saving}
            className="px-4 py-2 text-sm font-700 bg-[#0e7490] text-white hover:bg-[#0b5e75] disabled:opacity-60"
            data-testid="consent-reprompt-accept"
          >
            {saving ? "Saving…" : "I accept the updated Terms & Privacy"}
          </button>
        </div>
      </div>
    </div>
  );
}
